/**
 * ScheduleModal — redesigned Content Planner "Schedule publishing" dialog.
 *
 * Faithful port of the Claude Design handoff (Schedule Modal.html /
 * schedule-modal-live.jsx) into the real codebase. Two modes auto-switch on
 * item count:
 *   • Single  → one article: context strip + inline calendar + time wheel + quick picks
 *   • Bulk    → N articles spread across a date range with a visual distribution
 *               preview. Distribution mirrors the backend batch-schedule:
 *               dayOffset = floor(position * days / total)
 *
 * The modal owns its own date/time/days state and reports the chosen values
 * back through onConfirm({ date, days, timeOfDay }). It does NOT call any API —
 * ContentPlannerPage maps the payload onto the existing confirmSchedule flow,
 * so both endpoints and tenant batching stay untouched.
 *
 * Visuals use the codebase theme tokens (--color-*, --font-*) and reuse the
 * shared GradeBadge / StatusPill components rather than reinventing them.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  Globe,
  BarChart3,
} from 'lucide-react';
import type { CalendarItem } from '@/types/contentPlanner';
import { GradeBadge } from '@/components/content-planner/shared/GradeBadge';
import {
  StatusPill,
  deriveStatusVariant,
} from '@/components/content-planner/shared/StatusPill';

export interface ScheduleConfirmPayload {
  /** The selected publish date. For single, time is carried in `timeOfDay`. */
  date: Date;
  /** Number of days to spread the batch across (single = 1). */
  days: number;
  /** Publish time as "HH:mm". */
  timeOfDay: string;
}

interface ScheduleModalProps {
  items: CalendarItem[];
  onClose: () => void;
  onConfirm: (payload: ScheduleConfirmPayload) => void;
}

/* ---- date helpers (native Date, mirroring the design) ---- */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function fmtDate(d: Date): string {
  return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`;
}
function fmtDay(d: Date): string {
  return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
}
function weekday(d: Date): string {
  return WEEKDAYS[d.getDay()];
}
function fmtTime(d: Date): string {
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, '0')} ${ap}`;
}
function timeLabel(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  let hh = h % 12;
  if (hh === 0) hh = 12;
  return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
}
function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startWeek(d: Date): Date {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday-first
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function buildMonth(y: number, m: number): Date[] {
  const start = startWeek(new Date(y, m, 1));
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

/* ---- distribution: matches backend dayOffset = floor(pos*days/total) ---- */
interface Bucket {
  date: Date;
  items: CalendarItem[];
}
function distribute(items: CalendarItem[], startDate: Date, days: number): Bucket[] {
  const total = items.length;
  const byDay: CalendarItem[][] = Array.from({ length: days }, () => []);
  items.forEach((it, pos) => {
    const off = Math.min(days - 1, Math.floor((pos * days) / total));
    byDay[off].push(it);
  });
  return byDay.map((list, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    return { date: d, items: list };
  });
}

function titleFor(item: CalendarItem): string {
  return item.title || item.keyword || 'Untitled article';
}
function gradeFor(item: CalendarItem): string | null {
  if (item.grade) return item.grade;
  const s = item.quality_score;
  if (s == null) return null;
  if (s >= 90) return 'A';
  if (s >= 75) return 'B';
  if (s >= 60) return 'C';
  if (s >= 45) return 'D';
  return 'F';
}

function timezoneLabel(): string {
  const offsetMin = -new Date().getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '−';
  const abs = Math.abs(offsetMin);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const off = `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
  let city = '';
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    city = tz.split('/').pop()?.replace(/_/g, ' ') ?? '';
  } catch {
    city = '';
  }
  return city ? `${off} · ${city}` : off;
}

/* ---- shared inline styles (theme-token driven) ---- */
const BTN = {
  primary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    height: 36,
    padding: '0 18px',
    borderRadius: 8,
    background: 'var(--color-coral)',
    color: '#fff',
    border: '1px solid var(--color-coral)',
    fontWeight: 600,
    fontSize: 13,
    boxShadow:
      '0 1px 2px rgba(31,27,22,0.08), inset 0 1px 0 rgba(255,255,255,0.18)',
    cursor: 'pointer',
  } as React.CSSProperties,
  ghost: {
    height: 36,
    padding: '0 14px',
    borderRadius: 8,
    background: 'var(--color-paper)',
    color: 'var(--color-ink-2)',
    border: '1px solid var(--color-warm-border)',
    fontWeight: 550,
    fontSize: 13,
    cursor: 'pointer',
  } as React.CSSProperties,
  icon: {
    width: 30,
    height: 30,
    borderRadius: 7,
    background: 'transparent',
    border: '1px solid transparent',
    color: 'var(--color-ink-3)',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
  } as React.CSSProperties,
  nav: {
    width: 28,
    height: 28,
    borderRadius: 6,
    background: 'transparent',
    border: '1px solid transparent',
    color: 'var(--color-ink-2)',
    display: 'inline-grid',
    placeItems: 'center',
    cursor: 'pointer',
  } as React.CSSProperties,
};

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '.07em',
  textTransform: 'uppercase',
  color: 'var(--color-ink-3)',
};

function FieldLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ ...labelStyle, ...style }}>{children}</div>;
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--color-ink)',
        }}
      >
        {value}
      </span>
      <FieldLabel style={{ fontSize: 8.5 }}>{label}</FieldLabel>
    </div>
  );
}

function Wheel({
  value,
  min,
  max,
  step = 1,
  onChange,
  pad,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  pad?: boolean;
}) {
  const disp = pad ? String(value).padStart(2, '0') : String(value);
  const arrow: React.CSSProperties = {
    width: 18,
    height: 24,
    borderRadius: 4,
    background: 'transparent',
    border: 'none',
    color: 'var(--color-ink-3)',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <button
        type="button"
        style={arrow}
        onClick={() => onChange(value - step < min ? max : value - step)}
      >
        <ChevronLeft size={14} strokeWidth={1.6} />
      </button>
      <span
        style={{
          minWidth: 22,
          textAlign: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--color-ink)',
        }}
      >
        {disp}
      </span>
      <button
        type="button"
        style={arrow}
        onClick={() => onChange(value + step > max ? min : value + step)}
      >
        <ChevronRight size={14} strokeWidth={1.6} />
      </button>
    </div>
  );
}

/* =========================================================
   SINGLE — one article: context + date/time
========================================================= */
function SingleSchedule({
  item,
  onClose,
  onConfirm,
}: {
  item: CalendarItem;
  onClose: () => void;
  onConfirm: (payload: ScheduleConfirmPayload) => void;
}) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [date, setDate] = useState<Date>(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return d;
  });
  const [view, setView] = useState(() => ({ y: date.getFullYear(), m: date.getMonth() }));
  const cells = useMemo(() => buildMonth(view.y, view.m), [view]);

  const hour12 = ((date.getHours() + 11) % 12) + 1;
  const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
  const setHour = (h: number) => {
    const d = new Date(date);
    d.setHours(h);
    setDate(d);
  };
  const setMinute = (m: number) => {
    const d = new Date(date);
    d.setMinutes(m);
    setDate(d);
  };
  const pickDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(date.getHours(), date.getMinutes(), 0, 0);
    setDate(x);
  };

  const g = gradeFor(item);
  const kw = item.keyword;
  const wc = item.word_count ?? 0;

  const confirm = () => {
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    onConfirm({ date, days: 1, timeOfDay: `${hh}:${mm}` });
  };

  return (
    <div
      style={{
        width: 620,
        maxWidth: '100%',
        background: 'var(--color-paper)',
        borderRadius: 14,
        boxShadow: '0 24px 64px rgba(31,27,22,0.18)',
        border: '1px solid var(--color-warm-border)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px 14px',
          borderBottom: '1px solid var(--color-warm-border)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            Schedule publishing
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-ink-3)', marginTop: 1 }}>
            Set when this article auto-publishes to WordPress.
          </div>
        </div>
        <button type="button" onClick={onClose} style={BTN.icon} aria-label="Close">
          <X size={16} strokeWidth={1.5} />
        </button>
      </div>

      {/* Context strip — real CalendarItem fields */}
      <div
        style={{
          padding: '13px 20px',
          background: 'var(--color-paper-2)',
          borderBottom: '1px solid var(--color-warm-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14.5,
              fontWeight: 600,
              color: 'var(--color-ink)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {titleFor(item)}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 4,
              fontSize: 11,
              color: 'var(--color-ink-3)',
            }}
          >
            <span style={{ fontWeight: 550 }}>{item.template_name || 'Blog Post'}</span>
            {wc > 0 && (
              <>
                <span style={{ opacity: 0.5 }}>·</span>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{wc.toLocaleString()} words</span>
              </>
            )}
            {kw && (
              <>
                <span style={{ opacity: 0.5 }}>·</span>
                <span style={{ color: 'var(--color-coral-dark)', fontWeight: 600 }}>{kw}</span>
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {item.volume != null && <Metric label="Vol" value={item.volume.toLocaleString()} />}
          {item.difficulty != null && <Metric label="KD" value={item.difficulty} />}
          {g && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <GradeBadge grade={g} size="sm" />
              <FieldLabel style={{ fontSize: 8.5 }}>Grade</FieldLabel>
            </div>
          )}
          <StatusPill status={deriveStatusVariant(item)} size="sm" />
        </div>
      </div>

      {/* Body — calendar + time */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 212px' }}>
        <div style={{ padding: '14px 16px 16px', borderRight: '1px solid var(--color-warm-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
              {MONTHS[view.m]} {view.y}
            </div>
            <button
              type="button"
              style={BTN.nav}
              aria-label="Previous month"
              onClick={() =>
                setView((v) => ({ m: v.m === 0 ? 11 : v.m - 1, y: v.m === 0 ? v.y - 1 : v.y }))
              }
            >
              <ChevronLeft size={14} strokeWidth={1.4} />
            </button>
            <button
              type="button"
              style={BTN.nav}
              onClick={() => setView({ y: today.getFullYear(), m: today.getMonth() })}
            >
              <span style={{ fontSize: 11, color: 'var(--color-ink-2)', padding: '0 2px' }}>Today</span>
            </button>
            <button
              type="button"
              style={BTN.nav}
              aria-label="Next month"
              onClick={() =>
                setView((v) => ({ m: v.m === 11 ? 0 : v.m + 1, y: v.m === 11 ? v.y + 1 : v.y }))
              }
            >
              <ChevronRight size={14} strokeWidth={1.4} />
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7,1fr)',
              gap: 2,
              marginBottom: 4,
            }}
          >
            {DOW.map((d, i) => (
              <div
                key={i}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--color-ink-3)',
                  textAlign: 'center',
                }}
              >
                {d}
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
            {cells.map((d, i) => {
              const isOther = d.getMonth() !== view.m;
              const isToday = sameDay(d, today);
              const isSel = sameDay(d, date);
              const isPast = d < today;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => !isPast && pickDay(d)}
                  disabled={isPast}
                  style={{
                    height: 34,
                    border: '1px solid transparent',
                    background: isSel
                      ? 'var(--color-coral)'
                      : isToday
                        ? 'var(--color-coral-light)'
                        : 'transparent',
                    color: isSel
                      ? '#fff'
                      : isPast || isOther
                        ? 'var(--color-ink-3)'
                        : 'var(--color-ink)',
                    borderRadius: 7,
                    fontSize: 12.5,
                    fontWeight: isSel || isToday ? 600 : 400,
                    cursor: isPast ? 'not-allowed' : 'pointer',
                    opacity: isPast ? 0.4 : 1,
                    position: 'relative',
                  }}
                >
                  {d.getDate()}
                  {isToday && !isSel && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: 4,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 3,
                        height: 3,
                        borderRadius: '50%',
                        background: 'var(--color-coral)',
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <FieldLabel>Time</FieldLabel>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'var(--color-paper)',
                border: '1px solid var(--color-warm-border)',
                borderRadius: 8,
                padding: '4px 6px',
                marginTop: 6,
              }}
            >
              <Wheel
                value={hour12}
                min={1}
                max={12}
                onChange={(v) => setHour(ampm === 'PM' ? (v === 12 ? 12 : v + 12) : v === 12 ? 0 : v)}
              />
              <span style={{ fontWeight: 600, color: 'var(--color-ink-3)' }}>:</span>
              <Wheel value={date.getMinutes()} min={0} max={59} step={15} pad onChange={setMinute} />
              <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto', gap: 2 }}>
                {(['AM', 'PM'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => {
                      const h = date.getHours();
                      if (p === 'AM' && h >= 12) setHour(h - 12);
                      if (p === 'PM' && h < 12) setHour(h + 12);
                    }}
                    style={{
                      padding: '2px 8px',
                      fontSize: 10,
                      border: '1px solid var(--color-warm-border)',
                      borderRadius: 5,
                      background: ampm === p ? 'var(--color-ink)' : 'var(--color-paper)',
                      color: ampm === p ? '#fff' : 'var(--color-ink-2)',
                      fontFamily: 'var(--font-mono)',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <FieldLabel>Quick pick</FieldLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
              {(
                [
                  [
                    'Tomorrow',
                    '9:00 AM',
                    () => {
                      const d = new Date(today);
                      d.setDate(d.getDate() + 1);
                      d.setHours(9, 0, 0, 0);
                      setDate(d);
                      setView({ y: d.getFullYear(), m: d.getMonth() });
                    },
                  ],
                  [
                    'This Friday',
                    '10:00 AM',
                    () => {
                      const d = new Date(today);
                      d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7 || 7));
                      d.setHours(10, 0, 0, 0);
                      setDate(d);
                      setView({ y: d.getFullYear(), m: d.getMonth() });
                    },
                  ],
                  [
                    'Next Monday',
                    '8:00 AM',
                    () => {
                      const d = new Date(today);
                      d.setDate(d.getDate() + ((1 - d.getDay() + 7) % 7 || 7));
                      d.setHours(8, 0, 0, 0);
                      setDate(d);
                      setView({ y: d.getFullYear(), m: d.getMonth() });
                    },
                  ],
                ] as [string, string, () => void][]
              ).map(([l, t, fn]) => (
                <button
                  key={l}
                  type="button"
                  onClick={fn}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '7px 9px',
                    borderRadius: 6,
                    background: 'transparent',
                    border: '1px solid transparent',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-paper-2)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <span style={{ flex: 1, textAlign: 'left', fontSize: 12, color: 'var(--color-ink)' }}>
                    {l}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--color-ink-3)',
                    }}
                  >
                    {t}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--color-ink-3)',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              marginTop: 'auto',
            }}
          >
            <Globe size={13} strokeWidth={1.2} /> {timezoneLabel()}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '14px 20px',
          borderTop: '1px solid var(--color-warm-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <FieldLabel>Publishes</FieldLabel>
          <span style={{ fontSize: 13.5, color: 'var(--color-ink)', fontWeight: 600, marginTop: 2 }}>
            {fmtDate(date)} · {fmtTime(date)}
          </span>
        </div>
        <button type="button" onClick={onClose} style={BTN.ghost}>
          Cancel
        </button>
        <button type="button" onClick={confirm} style={BTN.primary}>
          <Calendar size={15} strokeWidth={1.3} /> Schedule
        </button>
      </div>
    </div>
  );
}

/* =========================================================
   BULK — N articles spread over days (the core feature)
========================================================= */
function BulkSchedule({
  items,
  onClose,
  onConfirm,
}: {
  items: CalendarItem[];
  onClose: () => void;
  onConfirm: (payload: ScheduleConfirmPayload) => void;
}) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [start, setStart] = useState<Date>(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return d;
  });
  const [timeOfDay, setTimeOfDay] = useState('10:00');
  const [days, setDays] = useState(Math.min(items.length, 5));
  const [view, setView] = useState(() => ({ y: start.getFullYear(), m: start.getMonth() }));
  const [pickerOpen, setPickerOpen] = useState(false);
  const cells = useMemo(() => buildMonth(view.y, view.m), [view]);

  const buckets = useMemo(() => distribute(items, start, days), [items, start, days]);
  const endDate = buckets[buckets.length - 1].date;
  const maxPerDay = Math.max(...buckets.map((b) => b.items.length));

  return (
    <div
      style={{
        width: 720,
        maxWidth: '100%',
        background: 'var(--color-paper)',
        borderRadius: 14,
        boxShadow: '0 24px 64px rgba(31,27,22,0.18)',
        border: '1px solid var(--color-warm-border)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px 14px',
          borderBottom: '1px solid var(--color-warm-border)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            Schedule {items.length} articles
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-ink-3)', marginTop: 1 }}>
            Spread the batch across a date range — one publish time per day.
          </div>
        </div>
        <button type="button" onClick={onClose} style={BTN.icon} aria-label="Close">
          <X size={16} strokeWidth={1.5} />
        </button>
      </div>

      {/* Controls */}
      <div
        style={{
          padding: '16px 20px',
          display: 'grid',
          gridTemplateColumns: '1.3fr 1fr 1.1fr',
          gap: 14,
          borderBottom: '1px solid var(--color-warm-border)',
        }}
      >
        {/* Start date */}
        <div style={{ position: 'relative' }}>
          <FieldLabel>Start date</FieldLabel>
          <button
            type="button"
            onClick={() => setPickerOpen((o) => !o)}
            style={{
              marginTop: 6,
              width: '100%',
              height: 38,
              padding: '0 12px',
              borderRadius: 8,
              border: '1px solid ' + (pickerOpen ? 'var(--color-coral)' : 'var(--color-warm-border)'),
              background: 'var(--color-paper)',
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              fontSize: 13,
              color: 'var(--color-ink)',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <span style={{ display: 'flex', color: 'var(--color-ink-3)' }}>
              <Calendar size={15} strokeWidth={1.3} />
            </span>
            <span style={{ flex: 1, textAlign: 'left' }}>{fmtDate(start)}</span>
            <span style={{ display: 'flex', color: 'var(--color-ink-3)' }}>
              <ChevronRight size={14} strokeWidth={1.4} />
            </span>
          </button>
          {pickerOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 6,
                zIndex: 20,
                background: 'var(--color-paper)',
                border: '1px solid var(--color-warm-border)',
                borderRadius: 10,
                boxShadow: '0 16px 40px rgba(31,27,22,0.16)',
                padding: 12,
                width: 268,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ flex: 1, fontSize: 12.5, fontWeight: 600 }}>
                  {MONTHS[view.m]} {view.y}
                </div>
                <button
                  type="button"
                  style={BTN.nav}
                  aria-label="Previous month"
                  onClick={() =>
                    setView((v) => ({ m: v.m === 0 ? 11 : v.m - 1, y: v.m === 0 ? v.y - 1 : v.y }))
                  }
                >
                  <ChevronLeft size={14} strokeWidth={1.4} />
                </button>
                <button
                  type="button"
                  style={BTN.nav}
                  aria-label="Next month"
                  onClick={() =>
                    setView((v) => ({ m: v.m === 11 ? 0 : v.m + 1, y: v.m === 11 ? v.y + 1 : v.y }))
                  }
                >
                  <ChevronRight size={14} strokeWidth={1.4} />
                </button>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7,1fr)',
                  gap: 2,
                  marginBottom: 3,
                }}
              >
                {DOW.map((d, i) => (
                  <div
                    key={i}
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 9.5,
                      color: 'var(--color-ink-3)',
                      textAlign: 'center',
                    }}
                  >
                    {d}
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
                {cells.map((d, i) => {
                  const isOther = d.getMonth() !== view.m;
                  const isSel = sameDay(d, start);
                  const isPast = d < today;
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={isPast}
                      onClick={() => {
                        const x = new Date(d);
                        x.setHours(0, 0, 0, 0);
                        setStart(x);
                        setPickerOpen(false);
                      }}
                      style={{
                        height: 30,
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 12,
                        background: isSel ? 'var(--color-coral)' : 'transparent',
                        color: isSel
                          ? '#fff'
                          : isPast || isOther
                            ? 'var(--color-ink-3)'
                            : 'var(--color-ink)',
                        fontWeight: isSel ? 600 : 400,
                        cursor: isPast ? 'not-allowed' : 'pointer',
                        opacity: isPast ? 0.4 : 1,
                      }}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Time of day */}
        <div>
          <FieldLabel>Time of day</FieldLabel>
          <div
            style={{
              marginTop: 6,
              height: 38,
              padding: '0 10px',
              borderRadius: 8,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ display: 'flex', color: 'var(--color-ink-3)' }}>
              <Clock size={14} strokeWidth={1.3} />
            </span>
            <input
              type="time"
              step={900}
              value={timeOfDay}
              onChange={(e) => setTimeOfDay(e.target.value || '10:00')}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13,
                color: 'var(--color-ink)',
                fontWeight: 500,
                width: '100%',
              }}
            />
          </div>
        </div>

        {/* Spread over days stepper */}
        <div>
          <FieldLabel>Spread over days</FieldLabel>
          <div
            style={{
              marginTop: 6,
              height: 38,
              borderRadius: 8,
              border: '1px solid var(--color-warm-border)',
              background: 'var(--color-paper)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              aria-label="Fewer days"
              onClick={() => setDays((d) => Math.max(1, d - 1))}
              style={{
                width: 36,
                height: '100%',
                border: 'none',
                background: 'transparent',
                color: 'var(--color-ink-2)',
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
                borderRight: '1px solid var(--color-warm-border)',
              }}
            >
              <Minus size={14} strokeWidth={1.6} />
            </button>
            <div
              style={{
                flex: 1,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: 5,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'var(--color-ink)',
                }}
              >
                {days}
              </span>
              <span style={{ fontSize: 11, color: 'var(--color-ink-3)' }}>
                {days === 1 ? 'day' : 'days'}
              </span>
            </div>
            <button
              type="button"
              aria-label="More days"
              onClick={() => setDays((d) => Math.min(30, d + 1))}
              style={{
                width: 36,
                height: '100%',
                border: 'none',
                background: 'transparent',
                color: 'var(--color-ink-2)',
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
                borderLeft: '1px solid var(--color-warm-border)',
              }}
            >
              <Plus size={14} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </div>

      {/* Distribution preview — the centerpiece */}
      <div style={{ padding: '14px 20px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
          <span style={{ display: 'flex', color: 'var(--color-coral)' }}>
            <BarChart3 size={14} strokeWidth={1.5} />
          </span>
          <FieldLabel style={{ color: 'var(--color-ink-2)' }}>Publishing schedule</FieldLabel>
          <span style={{ fontSize: 11, color: 'var(--color-ink-3)' }}>
            · {timeLabel(timeOfDay)} each day
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(days, 7)}, 1fr)`,
            gap: 8,
            overflowX: days > 7 ? 'auto' : 'visible',
            paddingBottom: 4,
          }}
        >
          {buckets.map((b, i) => (
            <div
              key={i}
              style={{
                minWidth: days > 7 ? 120 : 0,
                background: 'var(--color-paper-2)',
                border: '1px solid var(--color-warm-border)',
                borderRadius: 10,
                padding: '8px 8px 9px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div>
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 9.5,
                      color: 'var(--color-ink-3)',
                      letterSpacing: '.05em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {weekday(b.date)}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-ink)' }}>
                    {fmtDay(b.date)}
                  </div>
                </div>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: b.items.length ? 'var(--color-coral-dark)' : 'var(--color-ink-3)',
                    fontWeight: 600,
                  }}
                >
                  {b.items.length}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {b.items.length === 0 && (
                  <div
                    style={{
                      fontSize: 10.5,
                      color: 'var(--color-ink-3)',
                      fontStyle: 'italic',
                      padding: '6px 0',
                    }}
                  >
                    —
                  </div>
                )}
                {b.items.map((it) => {
                  const g = gradeFor(it);
                  return (
                    <div
                      key={it.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        background: 'var(--color-paper)',
                        border: '1px solid var(--color-warm-border)',
                        borderRadius: 7,
                        padding: '5px 7px',
                      }}
                    >
                      {g ? <GradeBadge grade={g} size="sm" /> : <span style={{ width: 18 }} />}
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 11,
                          color: 'var(--color-ink)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={titleFor(it)}
                      >
                        {titleFor(it)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '14px 20px',
          borderTop: '1px solid var(--color-warm-border)',
          marginTop: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <FieldLabel>Summary</FieldLabel>
          <span style={{ fontSize: 13, color: 'var(--color-ink)', fontWeight: 600, marginTop: 2 }}>
            {items.length} articles · {fmtDay(start)}
            {days > 1 ? `–${fmtDay(endDate)}` : ''} · {timeLabel(timeOfDay)}
            <span style={{ color: 'var(--color-ink-3)', fontWeight: 500 }}>
              {'  '}({maxPerDay}/day max)
            </span>
          </span>
        </div>
        <button type="button" onClick={onClose} style={BTN.ghost}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm({ date: start, days, timeOfDay })}
          style={BTN.primary}
        >
          <Calendar size={15} strokeWidth={1.3} /> Schedule {items.length}
        </button>
      </div>
    </div>
  );
}

/* =========================================================
   Root — overlay + mode switch (portal to body)
========================================================= */
export const ScheduleModal: React.FC<ScheduleModalProps> = ({ items, onClose, onConfirm }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (items.length === 0) return null;
  const bulk = items.length > 1;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Schedule publishing"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(31,27,22,0.42)',
        backdropFilter: 'blur(2px)',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        animation: 'smFade .15s ease',
        overflow: 'auto',
      }}
    >
      <style>{`@keyframes smFade{from{opacity:0}to{opacity:1}}@keyframes smPop{from{opacity:0;transform:translateY(8px) scale(.985)}to{opacity:1;transform:none}}`}</style>
      <div onClick={(e) => e.stopPropagation()} style={{ animation: 'smPop .18s cubic-bezier(.2,.7,.3,1)' }}>
        {bulk ? (
          <BulkSchedule items={items} onClose={onClose} onConfirm={onConfirm} />
        ) : (
          <SingleSchedule item={items[0]} onClose={onClose} onConfirm={onConfirm} />
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ScheduleModal;
